
import Quill, { Parchment } from 'quill';
import { AlignStyle } from 'quill/formats/align';
import { BackgroundStyle } from 'quill/formats/background';
import { ColorStyle } from 'quill/formats/color';
import { DirectionStyle } from 'quill/formats/direction';
import { FontStyle } from 'quill/formats/font';

export type ToolbarConfig = {
  allowPdf?: boolean;
  useTables?: boolean;
};

export const defaultToolbarBuilder = (config?: ToolbarConfig) => [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],

  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],

  [{ size: [false, 'large'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],

  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ align: [] }],
  ['link', 'image', 'video', ...(config?.allowPdf ? ['pdf'] : [])],
  ...(config?.useTables
    ? [[
      'table_insert', 'table_append_row', 'table_append_column',
      'table_delete_row', 'table_delete_column', 'table_delete_table']]
    : []),
];

export const defaultToolbar = defaultToolbarBuilder();

class IndentAttributor extends Parchment.StyleAttributor {
  override add(node: HTMLElement, value: unknown) {
    if (value === 0) {
      this.remove(node);
      return true;
    } else {
      return super.add(node, `${value}em`);
    }
  }
}

const IndentStyle = new IndentAttributor('indent', 'text-indent', {
  scope: Parchment.Scope.BLOCK,
  whitelist: ['1em', '2em', '3em', '4em', '5em', '6em', '7em', '8em', '9em'],
});

class SizeAttributor extends Parchment.StyleAttributor {
  override add(node: HTMLElement, value: unknown) {
    if (value === 'default') {
      this.remove(node);
      return true;
    } else {
      return super.add(node, value);
    }
  }

  override value(node: HTMLElement) {
    return super.value(node) || 'default';
  }
}

const SizeStyle = new SizeAttributor('size', 'font-size', {
  scope: Parchment.Scope.INLINE,
  whitelist: ['small', 'medium', 'large', 'huge'],
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BlockEmbed = Quill.import('blots/block/embed') as any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ImageEmbed = Quill.import('formats/image') as any;

type FileUploadElement = {
  src?: string;
  key?: string;
};

class PDFBlot extends BlockEmbed {
  static blotName = 'pdf';

  static tagName = 'embed';

  static create(value: FileUploadElement | unknown) {
    const isObject = typeof value === 'object' && !Array.isArray(value) && value !== null;
    if (isObject) {
      const { src, key } = value as FileUploadElement;
      const node = super.create(value);
      node.setAttribute('src', src);
      node.setAttribute('data-file-key', key);
      node.setAttribute('type', 'application/pdf');
      node.setAttribute('width', '100%');
      node.setAttribute('height', '1000px');
      return node;
    }
    return super.create(value);
  }

  static value(node: HTMLElement): FileUploadElement {
    const fileSrc = node.getAttribute('src') ?? undefined;
    const fileKey = node.getAttribute('data-file-key') ?? undefined;
    return { src: fileSrc, key: fileKey };
  }
}

class ImageBlot extends ImageEmbed {
  static blotName = 'image';
  static tagName = 'img';

  static create(value: FileUploadElement | unknown) {
    const isObject = typeof value === 'object' && !Array.isArray(value) && value !== null;
    if (isObject) {
      const { src, key } = value as FileUploadElement;
      const node = super.create(src); // Pass just src to parent
      node.setAttribute('src', src);
      node.setAttribute('style', 'max-width: 100%');
      if (key) {
        node.setAttribute('data-file-key', key);
      }
      return node;
    }
    return super.create(value);
  }

  static value(node: HTMLElement): FileUploadElement | unknown {
    const fileSrc = node.getAttribute('src') ?? undefined;
    const fileKey = node.getAttribute('data-file-key') ?? undefined;
    console.log('Enhanced ImageBlot.value called, src:', fileSrc, 'key:', fileKey);
    if (!fileKey) {
      return fileSrc;
    }
    return { src: fileSrc, key: fileKey };
  }
}

const icons = Quill.import('ui/icons') as { [key: string]: string };
icons['pdf'] = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-file-earmark-pdf" viewBox="0 0 16 16"><path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/><path d="M4.603 14.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.7 5.7 0 0 1-.911-.95 11.7 11.7 0 0 0-1.997.406 11.3 11.3 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.8.8 0 0 1-.58.029m1.379-1.901q-.25.115-.459.238c-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361q.016.032.026.044l.035-.012c.137-.056.355-.235.635-.572a8 8 0 0 0 .45-.606m1.64-1.33a13 13 0 0 1 1.01-.193 12 12 0 0 1-.51-.858 21 21 0 0 1-.5 1.05zm2.446.45q.226.245.435.41c.24.19.407.253.498.256a.1.1 0 0 0 .07-.015.3.3 0 0 0 .094-.125.44.44 0 0 0 .059-.2.1.1 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a4 4 0 0 0-.612-.053zM8.078 7.8a7 7 0 0 0 .2-.828q.046-.282.038-.465a.6.6 0 0 0-.032-.198.5.5 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822q.036.167.09.346z"/></svg>';
icons['table_insert'] = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-table" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z"/></svg>';
icons['table_append_row'] = '<svg xmlns="http://www.w3.org/2000/svg" width="18px" height="16px" viewBox="0 -960 960 960" fill="currentColor"><path d="M200-560h560v-240H200v240Zm-80 400v-720h720v720H680v-80h80v-240H200v240h80v80H120Zm360-320Zm0-80v80-80Zm0 0ZM440-80v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80Z"/></svg>';
icons['table_append_column'] = '<svg xmlns="http://www.w3.org/2000/svg" width="18px" height="16px" viewBox="0 -960 960 960" fill="currentColor"><path d="M160-760v560h240v-560H160ZM80-120v-720h720v160h-80v-80H480v560h240v-80h80v160H80Zm400-360Zm-80 0h80-80Zm0 0Zm320 120v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80Z"/></svg>';
icons['table_delete_row'] = '<svg xmlns="http://www.w3.org/2000/svg" width="16px" height="20px" viewBox="371.296 -185.47 720.004 720.0031" fill="currentColor"><path d="M 451.296 134.533 L 1011.3 134.533 L 1011.3 -105.47 L 451.296 -105.47 L 451.296 134.533 Z M 371.296 534.533 L 371.296 -185.47 L 1091.3 -185.47 L 1091.3 534.533 L 931.296 534.533 L 931.296 454.533 L 1011.3 454.533 L 1011.3 214.533 L 451.296 214.533 L 451.296 454.533 L 531.296 454.533 L 531.296 534.533 L 371.296 534.533 Z M 731.296 214.533 Z M 731.296 134.533 L 731.296 214.533 L 731.296 134.533 Z M 731.296 134.533 Z M 771.296 386.533 L 691.296 386.533 L 611.296 386.533 L 611.296 306.533 L 691.296 306.533 L 771.296 306.533 L 851.296 306.533 L 851.296 386.533 L 771.296 386.533 Z" style="stroke-width: 1;"/></svg>';
icons['table_delete_column'] = '<svg xmlns="http://www.w3.org/2000/svg" width="16px" height="20px" viewBox="109.337 152.896 242.293 218.064" fill="currentColor"><path d="M 133.565 177.124 L 133.565 346.73 L 206.248 346.73 L 206.248 177.124 L 133.565 177.124 Z M 109.337 370.96 L 109.337 152.896 L 327.401 152.896 L 327.401 201.351 L 303.171 201.351 L 303.171 177.124 L 230.476 177.124 L 230.476 346.73 L 303.171 346.73 L 303.171 322.501 L 327.401 322.501 L 327.401 370.96 L 109.337 370.96 Z M 230.476 261.929 Z M 206.248 261.929 L 230.476 261.929 L 206.248 261.929 Z M 206.248 261.929 Z M 327.401 274.05 L 303.171 274.05 L 278.947 274.05 L 278.947 249.807 L 303.171 249.807 L 327.401 249.807 L 351.63 249.807 L 351.63 274.05 L 327.401 274.05 Z" style="stroke-width: 1;" transform="matrix(1, 0, 0, 1, 5.684341886080802e-14, 0)"/></svg>';
icons['table_delete_table'] = '<svg xmlns="http://www.w3.org/2000/svg" width="18px" height="32px" viewBox="0 -960 960 960" fill="currentColor"><path d="M333-200v-133H200v133h133Zm214 0v-100l-33-33H413v133h134Zm80 0Zm116-133Zm-410-80v-101l-33-33H200v134h133Zm80 0Zm347 0v-134H627v99l35 35h98ZM529-547Zm-329-80Zm347 0v-133H413v98l35 35h99Zm213 0v-133H627v133h133ZM316-760Zm524 525L235-840h525q33 0 56.5 23.5T840-760v525ZM200-120q-33 0-56.5-23.5T120-200v-640l720 720H200Zm619 92L28-820l56-56L876-84l-57 56Z"/></svg>';

export function initQuill() {
  Quill.register(AlignStyle, true);
  Quill.register(BackgroundStyle, true);
  Quill.register(ColorStyle, true);
  Quill.register(DirectionStyle, true);
  Quill.register(FontStyle, true);
  Quill.register(IndentStyle, true);
  Quill.register(SizeStyle, true);
  Quill.register(PDFBlot, true);
  Quill.register(ImageBlot, true);
  Quill.register('table_insert', true);
  Quill.register('table_append_row', true);
  Quill.register('table_append_column', true);
  Quill.register('table_delete_row', true);
  Quill.register('table_delete_column', true);
  Quill.register('table_delete_table', true);
}